import {
  Badge,
  Descriptions,
  List,
  Modal,
  Tag,
  Typography,
  Tabs,
  theme,
} from "antd";
import React, { useEffect, useState } from "react";
import { IBusinessFull } from "../../../../interfaces/businessFull.interface";
import { UserOutlined } from "@ant-design/icons";
import styles from "./SalesBusinessActivityDetails.module.css";
import Loader from "../../../common/loader/Loader";
import {
  getBusinessUsersThunk,
  selectCurrentBusiness,
  selectCurrentBusinessUsers,
} from "../../../businesses/businessesSlice";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import type { TabsProps } from "antd";

const { Paragraph } = Typography;

interface IPropsBusinessModel {
  visible: boolean;
  setVisible: any;
}

export const MissingTag = <Badge count={"Missing"} />;

// Tab Components
const GeneralTab: React.FC<{ business: IBusinessFull }> = ({ business }) => {
  const getFullDate = (date: string): string => {
    if (!date) return "";
    const dateAndTime = date.split("T");
    return dateAndTime[0].split("-").reverse().join("-");
  };

  const getFullAddress = (address: {
    street?: string;
    houseNumber?: string;
    postcode?: string;
    city?: string;
    country?: string;
    houseNumberSuffix?: string;
  }): string => {
    if (!address?.street) return "";
    const {
      street = "",
      houseNumber = "",
      houseNumberSuffix = "",
      postcode = "",
      city = "",
      country = "",
    } = address;

    const fullAddress = `${street} ${houseNumber} ${houseNumberSuffix}, ${postcode} ${city} ${country}`;
    return fullAddress;
  };

  return (
    <Descriptions title="General">
      <Descriptions.Item label="Onboarded On">
        {getFullDate(business.createdAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Internal ID">{business._id}</Descriptions.Item>
      <Descriptions.Item label="Last Updated On">
        {getFullDate(business.updatedAt)}
      </Descriptions.Item>
      <Descriptions.Item label="Status">
        {getFullDate(business.status)}
      </Descriptions.Item>
      <Descriptions.Item label="Type">
        {getFullDate(business.businessType)}
      </Descriptions.Item>
      <Descriptions.Item label="Industry">
        {getFullDate(business.industry)}
      </Descriptions.Item>
      <Descriptions.Item label="Address">
        {getFullAddress(business.address)}
      </Descriptions.Item>
      <Descriptions.Item label="Referral Code">
        {business.referralCode}
      </Descriptions.Item>
      <Descriptions.Item label="Incorporation Date">
        {business.incorporationDate || "Unknown"}
      </Descriptions.Item>
    </Descriptions>
  );
};

const MerchantContactTab: React.FC<{ business: IBusinessFull }> = ({
  business,
}) => {
  return (
    <Descriptions title="Merchant Contact Info">
      <Descriptions.Item label="UserName">
        {business.rootUser?.email}
      </Descriptions.Item>
      <Descriptions.Item label="Telephone">
        {business.rootUser.phone
          ? `+${business.rootUser.phone.countryCode} ${business.rootUser.phone.number}`
          : "N/A"}
      </Descriptions.Item>
      <Descriptions.Item label="Name">
        {business.rootUser?.name}
      </Descriptions.Item>
      <Descriptions.Item label="Address">
        {!business.address?.street
          ? MissingTag
          : `${business.address?.street} ${business.address?.houseNumber},${business.address?.postcode} ${business.address?.city} , ${business.address?.country}`}
      </Descriptions.Item>
    </Descriptions>
  );
};

const ServiceFeesTab: React.FC<{ business: IBusinessFull }> = ({
  business,
}) => {
  return (
    <Descriptions title="Merchant Service Fees">
      {business.merchantServiceChargeFees.map((f) => (
        <Descriptions.Item key={f?.name} label={f?.name}>
          {f?.value}
          {f?.valueType === "Percentage" ? (
            "%"
          ) : (
            <Tag style={{ marginLeft: "8px" }}>FIXED</Tag>
          )}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};

const TransactionVolumesTab: React.FC<{ business: IBusinessFull }> = ({
  business,
}) => {
  return (
    <Descriptions title="Expected Transaction Volumes">
      <Descriptions.Item label="Average Monthly Volume ">
        {business.transactionInformation?.averageMonthlyVolume}
      </Descriptions.Item>
      <Descriptions.Item label="Average Monthly Count">
        {
          business.transactionInformation
            ?.expectedAverageMonthlyNumberOfTransactions
        }
      </Descriptions.Item>
      <Descriptions.Item label="Average Transaction Value">
        {business.transactionInformation?.expectedAverageTransactionAmount}
      </Descriptions.Item>
      <Descriptions.Item label="Maximum Transaction Amount">
        {business.transactionInformation?.expectedMaximumTransactionAmount}
      </Descriptions.Item>
    </Descriptions>
  );
};

const UsersTab: React.FC<{ businessUsers: any[] }> = ({ businessUsers }) => {
  return (
    <div>
      <Descriptions title="Users"></Descriptions>
      <List
        size="small"
        dataSource={businessUsers}
        renderItem={(item) => (
          <List.Item>
            <List.Item.Meta
              avatar={
                <>
                  <UserOutlined></UserOutlined>
                  <br />
                  {item.role}
                </>
              }
              title={<span>{item?.name || "Pending Invite"}</span>}
              description={item.role}
            ></List.Item.Meta>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "start",
                width: "300px",
              }}
            >
              <span> email: {item.email}</span>
              <span> Phone: {item.phone?.number || "None"} </span>
            </div>
          </List.Item>
        )}
      />
    </div>
  );
};

export default function SalesBusinessActivityDetails({
  visible,
  setVisible,
}: IPropsBusinessModel) {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(false);
  const business = useAppSelector(selectCurrentBusiness);
  const [currentBusiness, setCurrentBusiness] = useState<IBusinessFull>();
  const businessUsers = useAppSelector(selectCurrentBusinessUsers);

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (business) {
      setCurrentBusiness(business);
      fetchBusinessUsersDetails(business._id);
    }
  }, [business]);

  const fetchBusinessUsersDetails = async (businessId: string) => {
    if (!business?._id) return;
    setLoading(true);
    await dispatch(getBusinessUsersThunk(business?._id));
    setLoading(false);
  };

  const handleOk = () => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setVisible(false);
    }, 3000);
  };

  const handleCancel = () => {
    setVisible(false);
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: "General",
      children: currentBusiness ? (
        <GeneralTab business={currentBusiness} />
      ) : null,
    },
    {
      key: "2",
      label: "Merchant Contact Info",
      children: currentBusiness ? (
        <MerchantContactTab business={currentBusiness} />
      ) : null,
    },
    {
      key: "3",
      label: "Merchant Service Fees",
      children: currentBusiness ? (
        <ServiceFeesTab business={currentBusiness} />
      ) : null,
    },
    {
      key: "4",
      label: "Expected Transaction Volumes",
      children: currentBusiness ? (
        <TransactionVolumesTab business={currentBusiness} />
      ) : null,
    },
    {
      key: "5",
      label: "Users",
      children: businessUsers ? (
        <UsersTab businessUsers={businessUsers} />
      ) : null,
    },
  ];

  return (
    <Modal
      title={`Business Activity Details - ${business?.businessName || ""}`}
      open={visible}
      onCancel={handleCancel}
      width={1000}
      footer={null}
    >
      {currentBusiness?._id && currentBusiness.rootUser?._id && !loading && (
        <div>
          <Tabs
            defaultActiveKey="1"
            items={items}
            type="card"
            style={{ marginTop: token.marginMD }}
          />
        </div>
      )}

      {loading && (
        <div className={styles.spin_container} style={{ height: "500px" }}>
          <Loader text="Fetching Business Details" />
        </div>
      )}
    </Modal>
  );
}
